import React, { useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { createRecord, listManufacturers, listModels, listRecords, toApiError, updateRecord } from '../../services/pocApi';
import useApiResource from '../../components/poc/useApiResource';
import { usePocAccess } from '../../components/poc/PocAccess';
import { LifecycleActions, LifecycleTimeline } from '../../components/poc/RecordLifecycle';
import {
    ADMIN_LINKS, AccessDenied, DetailList, Disclaimer, EmptyState, ErrorNotice, Field, LoadingState, PageHeader, Panel,
    StatusBadge, SubNav,
} from '../../components/poc/PocUi';
import { PATTERNS, SEGMENTS, SOURCE_TYPES, formatAmount, isRealDate, manufacturerName, normalizeAmount } from '../../components/poc/pocFormat';

const STATUSES = ['DRAFT', 'ACTIVE', 'RETIRED'];
export const THRESHOLD_EXPLANATION =
    'An eligible claim above the configured authorization threshold is referred for manual review. It is not automatically rejected.';
const EMPTY_RULE = {
    rule_id: '', manufacturer_code: '', model_code: '', segment: '', threshold_amount: '', currency: 'USD', priority: '100',
    effective_from: '', effective_to: '', source_type: 'ICMS_DEMO_DEFAULT', source_reference: '',
};

const str = (v) => (v === null || v === undefined ? '' : String(v));

export function ruleToForm(rule, { keepId }) {
    return { ...Object.fromEntries(Object.keys(EMPTY_RULE).map((k) => [k, str(rule[k])])), rule_id: keepId ? rule.rule_id : '' };
}

/** AuthorizationRuleCreateIn / AuthorizationRuleConfigIn body; the threshold is a decimal string, never a float. */
export function buildRuleBody(form, { includeId }) {
    const errors = {};
    const t = (k) => str(form[k]).trim();
    const body = {};
    if (includeId) {
        body.rule_id = t('rule_id').toUpperCase();
        if (!PATTERNS.recordId.test(body.rule_id)) errors.rule_id = '3-64 upper-case letters, digits, "_", "." or "-".';
    }
    body.manufacturer_code = t('manufacturer_code') || null;
    body.model_code = t('model_code') || null;
    body.segment = t('segment') || null;
    body.threshold_amount = normalizeAmount(t('threshold_amount'));
    if (!t('threshold_amount') || body.threshold_amount === null) errors.threshold_amount = 'Enter a non-negative amount with up to two decimals.';
    body.currency = t('currency').toUpperCase();
    if (!PATTERNS.currency.test(body.currency)) errors.currency = 'Use a three-letter currency code.';
    const priority = t('priority') || '100';
    body.priority = /^\d+$/.test(priority) && Number(priority) <= 1000000 ? Number(priority) : null;
    if (body.priority === null) errors.priority = 'Priority must be a whole number from 0 to 1,000,000.';
    body.effective_from = t('effective_from');
    if (!isRealDate(body.effective_from)) errors.effective_from = 'Enter a valid start date.';
    body.effective_to = t('effective_to') || null;
    if (body.effective_to && !isRealDate(body.effective_to)) errors.effective_to = 'Enter a valid end date.';
    body.source_type = form.source_type;
    body.source_reference = t('source_reference') || null;
    return { body, errors };
}

function RuleForm({ mode, initial, manufacturers, onSaved, onCancel }) {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const models = useApiResource(
        (signal) => (form.manufacturer_code ? listModels(form.manufacturer_code, signal) : Promise.resolve([])),
        [form.manufacturer_code],
    );
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        if (saving) return;
        const { body, errors: found } = buildRuleBody(form, { includeId: mode === 'create' });
        setErrors(found);
        setError(null);
        if (Object.keys(found).length) return;
        setSaving(true);
        try {
            onSaved(mode === 'create' ? await createRecord('rule', body) : await updateRecord('rule', initial.rule_id, body));
        } catch (err) {
            const apiError = toApiError(err);
            setErrors((prev) => ({ ...prev, ...apiError.fieldErrors }));
            setError(apiError);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Panel title={mode === 'create' ? 'New Draft Authorization Rule' : `Edit Draft ${initial.rule_id}`}>
            <form onSubmit={save} noValidate className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {mode === 'create' && (
                        <Field label="Rule ID" required error={errors.rule_id}>
                            <input type="text" className="form-input" value={form.rule_id} onChange={set('rule_id')} maxLength={64} />
                        </Field>
                    )}
                    <Field label="Manufacturer" hint="Empty applies to every manufacturer.">
                        <select className="form-select" value={form.manufacturer_code}
                                onChange={(e) => setForm((f) => ({ ...f, manufacturer_code: e.target.value, model_code: '' }))}>
                            <option value="">All manufacturers</option>
                            {manufacturers.map((m) => <option key={m.manufacturer_code} value={m.manufacturer_code}>{m.manufacturer_name} ({m.manufacturer_code})</option>)}
                        </select>
                    </Field>
                    <Field label="Model">
                        <select className="form-select" value={form.model_code} onChange={set('model_code')} disabled={!form.manufacturer_code}>
                            <option value="">All models</option>
                            {form.model_code && !(models.data || []).some((m) => m.model_code === form.model_code) && <option value={form.model_code}>{form.model_code}</option>}
                            {(models.data || []).map((m) => <option key={m.model_code} value={m.model_code}>{(m.model_name || '').trim() || m.model_code} ({m.model_code})</option>)}
                        </select>
                    </Field>
                    <Field label="Vehicle Segment">
                        <select className="form-select" value={form.segment} onChange={set('segment')}>
                            <option value="">Any segment</option>
                            {SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Threshold Amount" required error={errors.threshold_amount} hint="Amounts above this are referred for manual review.">
                        <input type="text" inputMode="decimal" className="form-input" value={form.threshold_amount} onChange={set('threshold_amount')} />
                    </Field>
                    <Field label="Currency" required error={errors.currency}>
                        <input type="text" className="form-input" value={form.currency} onChange={set('currency')} maxLength={3} />
                    </Field>
                    <Field label="Priority" error={errors.priority} hint="Among equally specific rules, the lower number wins.">
                        <input type="text" inputMode="numeric" className="form-input" value={form.priority} onChange={set('priority')} />
                    </Field>
                    <Field label="Effective From" required error={errors.effective_from}><input type="date" className="form-input" value={form.effective_from} onChange={set('effective_from')} /></Field>
                    <Field label="Effective To" error={errors.effective_to}><input type="date" className="form-input" value={form.effective_to} onChange={set('effective_to')} /></Field>
                    <Field label="Source Type">
                        <select className="form-select" value={form.source_type} onChange={set('source_type')}>
                            {SOURCE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Source Reference" className="md:col-span-3">
                        <input type="text" className="form-input" value={form.source_reference} onChange={set('source_reference')} maxLength={500} />
                    </Field>
                </div>
                <ErrorNotice error={error} title="The draft was not saved" />
                <div className="flex gap-3">
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Draft'}</button>
                    <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
                </div>
            </form>
        </Panel>
    );
}

export default function AuthorizationRules() {
    const { isAdmin, checked } = usePocAccess();
    const [status, setStatus] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(null);
    const [auditVersion, setAuditVersion] = useState(0);
    // Rules (and their thresholds) are requested only once the administrator role is confirmed.
    const rules = useApiResource((signal) => (isAdmin ? listRecords('rule', { status }, signal) : Promise.resolve(null)), [status, isAdmin]);
    const manufacturers = useApiResource((signal) => (isAdmin ? listManufacturers(signal) : Promise.resolve([])), [isAdmin]);

    if (!checked) return <LoadingState label="Checking access…" />;
    if (!isAdmin) return <AccessDenied what="authorization rule administration" />;

    const names = manufacturers.data || [];
    const items = rules.data?.items || [];
    const selected = items.find((r) => r.rule_id === selectedId);
    const scope = (r) => (r.manufacturer_code ? manufacturerName(names, r.manufacturer_code) : 'All manufacturers');
    const saved = (rule) => {
        setForm(null);
        setStatus('');
        setSelectedId(rule.rule_id);
        rules.reload();
    };

    return (
        <div className="space-y-8 max-w-7xl mx-auto">
            <SubNav label="Warranty administration" links={ADMIN_LINKS} />
            <PageHeader title="Authorization Rules" subtitle={THRESHOLD_EXPLANATION}
                        actions={<>
                            <button type="button" className="btn btn-primary gap-2" onClick={() => setForm({ mode: 'create', initial: EMPTY_RULE })}>
                                <Plus className="w-4 h-4" aria-hidden="true" />Create Draft Rule
                            </button>
                            <button type="button" className="btn btn-secondary gap-2" onClick={rules.reload} disabled={rules.loading}>
                                <RefreshCw className="w-4 h-4" aria-hidden="true" />Refresh
                            </button>
                        </>} />
            <p className="text-xs text-text-muted">A threshold is not a warranty coverage limit: it only decides whether an eligible claim needs manual authorization. Threshold values are visible to administrators only.</p>
            <Disclaimer />

            {form && <RuleForm key={`${form.mode}-${form.initial.rule_id}`} mode={form.mode} initial={form.initial} manufacturers={names}
                               onSaved={saved} onCancel={() => setForm(null)} />}

            <Panel title="Rules" actions={
                <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-text-muted">
                    Status
                    <select className="form-select !py-1 !px-2 !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">All</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
            }>
                {rules.loading && <LoadingState label="Loading authorization rules…" />}
                <ErrorNotice error={rules.error} onRetry={rules.reload} />
                {!rules.loading && rules.data && (items.length ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <caption className="sr-only">Authorization rules</caption>
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-text-muted">
                                    {['Rule ID', 'Manufacturer', 'Model', 'Segment', 'Threshold', 'Effective', 'Priority', 'Status', ''].map((h) => (
                                        <th key={h} scope="col" className="py-2 pr-4 font-semibold">{h || <span className="sr-only">Actions</span>}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((r) => (
                                    <tr key={r.rule_id} className={`border-t border-line ${r.rule_id === selectedId ? 'bg-background' : ''}`}>
                                        <td className="py-2 pr-4 font-semibold whitespace-nowrap">{r.rule_id}</td>
                                        <td className="py-2 pr-4">{scope(r)}</td>
                                        <td className="py-2 pr-4">{r.model_code || 'All models'}</td>
                                        <td className="py-2 pr-4">{r.segment || 'Any'}</td>
                                        <td className="py-2 pr-4 whitespace-nowrap">{formatAmount(r.threshold_amount, r.currency)}</td>
                                        <td className="py-2 pr-4 whitespace-nowrap">{r.effective_from} – {r.effective_to || 'open'}</td>
                                        <td className="py-2 pr-4">{r.priority}</td>
                                        <td className="py-2 pr-4"><StatusBadge status={r.status} /></td>
                                        <td className="py-2">
                                            <button type="button" className="text-xs font-semibold uppercase tracking-widest underline underline-offset-2"
                                                    onClick={() => setSelectedId(r.rule_id)} aria-label={`View rule ${r.rule_id}`}>View</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {rules.data.total > items.length && <p className="text-[11px] text-text-muted mt-3">Showing the first {items.length} of {rules.data.total} rules.</p>}
                    </div>
                ) : <EmptyState>No authorization rules match this filter.</EmptyState>)}
            </Panel>

            {selected && (
                <Panel title={`Rule ${selected.rule_id}`} actions={<>
                    {selected.status === 'DRAFT' && (
                        <button type="button" className="btn btn-secondary !px-4 !py-2" onClick={() => setForm({ mode: 'edit', initial: ruleToForm(selected, { keepId: true }) })}>Edit Draft</button>
                    )}
                    <button type="button" className="btn btn-secondary !px-4 !py-2" onClick={() => setForm({ mode: 'create', initial: ruleToForm(selected, { keepId: false }) })}>
                        New Draft from This Rule
                    </button>
                </>}>
                    <div className="space-y-8">
                        <DetailList items={[
                            ['Status', <StatusBadge status={selected.status} />],
                            ['Manufacturer scope', scope(selected)],
                            ['Model scope', selected.model_code || 'All models'],
                            ['Vehicle segment', selected.segment || 'Any'],
                            ['Threshold', formatAmount(selected.threshold_amount, selected.currency)],
                            ['Effective', `${selected.effective_from} to ${selected.effective_to || 'open-ended'}`],
                            ['Priority', selected.priority],
                            ['Source', `${selected.source_type}${selected.source_reference ? ` — ${selected.source_reference}` : ''}`],
                        ]} />
                        <div>
                            <h4 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-3">Lifecycle</h4>
                            <LifecycleActions kind="rule" id={selected.rule_id} status={selected.status} label={`${selected.rule_id} — ${scope(selected)}`}
                                              onChanged={() => { setAuditVersion((v) => v + 1); rules.reload(); }} />
                        </div>
                        <div>
                            <h4 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted mb-3">Lifecycle audit</h4>
                            <LifecycleTimeline kind="rule" id={selected.rule_id} version={auditVersion} />
                        </div>
                    </div>
                </Panel>
            )}
        </div>
    );
}
